import React, { memo } from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

export interface PageSkeletonProps {
	rowCount?: number;
	columnCount?: number;
}

const PageSkeleton = memo<PageSkeletonProps>(({ rowCount = 10, columnCount = 5 }) => {
	const cols = Array.from({ length: Math.max(1, columnCount) });
	const rows = Array.from({ length: Math.max(1, rowCount) });

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2.5, gap: 1.5 }}>
			{/* Page header toolbar */}
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Stack direction="row" alignItems="center" gap={1.5}>
					<Skeleton variant="rounded" width={160} height={32} />
					<Skeleton variant="rounded" width={108} height={32} />
					<Skeleton variant="rounded" width={88} height={32} />
				</Stack>
				<Stack direction="row" alignItems="center" gap={1}>
					<Skeleton variant="rounded" width={200} height={36} />
					<Skeleton variant="rounded" width={80} height={36} />
					<Skeleton variant="rounded" width={80} height={36} />
				</Stack>
			</Stack>

			{/* Table */}
			<Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
				{/* Header row */}
				<Stack
					direction="row"
					alignItems="center"
					gap={2}
					sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
				>
					<Skeleton variant="rounded" width={18} height={18} sx={{ flexShrink: 0 }} />
					<Skeleton variant="rounded" width={18} height={18} sx={{ flexShrink: 0 }} />
					{cols.map((_, i) => (
						<Skeleton key={i} variant="text" width={50} height={16} sx={{ flex: i === 0 ? 0.7 : 1 }} />
					))}
				</Stack>

				{/* Data rows */}
				{rows.map((_, rowIdx) => (
					<Stack
						key={rowIdx}
						direction="row"
						alignItems="center"
						gap={2}
						sx={{
							px: 1.5,
							py: 1.25,
							borderBottom: rowIdx < rowCount - 1 ? '1px solid' : 'none',
							borderColor: 'divider',
						}}
					>
						<Skeleton variant="rounded" width={18} height={18} sx={{ flexShrink: 0 }} />
						<Skeleton variant="rounded" width={18} height={18} sx={{ flexShrink: 0 }} />
						{cols.map((_, colIdx) =>
							colIdx === columnCount - 1 ? (
								<Box key={colIdx} sx={{ flex: 0.5 }}>
									<Skeleton variant="rounded" width={50} height={22} sx={{ borderRadius: '11px' }} />
								</Box>
							) : (
								<Skeleton
									key={colIdx}
									variant="text"
									width={50}
									height={20}
									sx={{ flex: colIdx === 0 ? 0.7 : 1 }}
								/>
							)
						)}
					</Stack>
				))}
			</Box>
		</Box>
	);
});

PageSkeleton.displayName = 'PageSkeleton';

export { PageSkeleton };
export default PageSkeleton;
